// Ajuste de las etiquetas de cada fila (ContactTagList.razor.js del portal actual): se mide el contenido real (fuente,
// idioma y ancho) y lo que no entra pasa a «+N». Una sola pasada por cuadro para todas las listas, después del layout.

const bindings = new WeakSet<HTMLElement>();
/** Un solo observador para todas las listas: los cambios de ancho del mismo cuadro se ajustan juntos (un layout). */
let resized: ResizeObserver | null = null;
const observer = () =>
  (resized ??= new ResizeObserver((entries) => {
    const changed = entries
      .map((entry) => entry.target as HTMLElement)
      .filter((root) => root.isConnected && root.clientWidth !== widths.get(root));
    if (changed.length) layoutAll(changed);
  }));
const widths = new WeakMap<HTMLElement, number>();

type Line = { root: HTMLElement; chips: HTMLElement[]; more: HTMLElement; count: HTMLElement };

/** Ajusta varias listas leyendo la geometría una sola vez para todas (escribir, leer todo, escribir): antes cada lista
 * y cada vuelta del recorte (quitar una etiqueta y volver a medir) forzaba un layout de toda la tabla: 117–155 ms al
 * entrar a Contactos con 50 filas. El resultado es el mismo: lo que no entra pasa a «+N» y queda al menos una. */
function layoutAll(roots: HTMLElement[]) {
  const lines: Line[] = [];
  // Lectura de estilos antes de escribir nada: cuáles van en una sola línea (C17, C15 1.14.4).
  for (const root of roots) {
    if (!root.isConnected) continue;
    const more = root.querySelector<HTMLElement>("[data-tag-more]");
    const count = more?.querySelector<HTMLElement>("[data-tag-count]");
    if (!more || !count) continue;
    const chips = [...root.querySelectorAll<HTMLElement>("[data-tag-chip]")];
    if (getComputedStyle(root).flexWrap === "nowrap") lines.push({ root, chips, more, count });
    else wrapped(root, chips, more, count);
  }
  // 1 · Escribir: todas visibles, sin encogerse, y «+N» con su texto más ancho posible.
  for (const { chips, more, count } of lines) {
    for (const chip of chips) {
      if (chip.hidden) chip.hidden = false;
      chip.style.flexShrink = "0";
    }
    if (more.hidden) more.hidden = false;
    count.textContent = "+" + chips.length;
  }
  // 2 · Leer: un solo layout para todas las listas.
  const measured = lines.map(({ root, chips, more }) => ({
    available: root.clientWidth,
    gap: parseFloat(getComputedStyle(root).columnGap) || 0,
    sizes: chips.map((chip) => chip.offsetWidth),
    more: more.offsetWidth,
  }));
  // 3 · Escribir el resultado: cuántas entran y, si sobran, «+N» con «También: …» (C15 1.14.4).
  lines.forEach(({ root, chips, more, count }, index) => {
    const { available, gap, sizes, more: moreWidth } = measured[index]!;
    widths.set(root, available);
    for (const chip of chips) chip.style.flexShrink = "";
    if (!available) return;
    const span = (n: number) => sizes.slice(0, n).reduce((sum, size) => sum + size, 0) + gap * Math.max(0, n - 1);
    let visible = chips.length;
    if (span(visible) > available + 1) {
      visible = chips.length - 1;
      while (visible > 1 && span(visible) + gap + moreWidth > available + 1) visible--;
    }
    chips.forEach((chip, position) => {
      const hide = position >= visible;
      if (chip.hidden !== hide) chip.hidden = hide;
    });
    if (visible === chips.length) {
      more.hidden = true;
      return;
    }
    count.textContent = "+" + (chips.length - visible);
    const template = root.dataset.tagsAlso;
    if (template) {
      const names = chips.slice(visible).map((chip) => chip.getAttribute("aria-label") ?? "");
      const also = template.replace("{0}", names.join(", "));
      if (more.title !== also) {
        more.title = also;
        more.setAttribute("aria-label", also);
      }
    }
  });
}

/** Lista que puede ocupar dos líneas (ficha): como antes, hasta dos líneas y el resto en «+N». */
function wrapped(root: HTMLElement, chips: HTMLElement[], more: HTMLElement, count: HTMLElement) {
  widths.set(root, root.clientWidth);
  if (!root.clientWidth) return;
  chips.forEach((chip) => {
    if (chip.hidden) chip.hidden = false;
  });
  if (!more.hidden) more.hidden = true;
  const rows = () => [...new Set(chips.filter((chip) => !chip.hidden).map((chip) => chip.offsetTop))];
  if (rows().length <= 2) return;
  more.hidden = false;
  for (let visible = chips.length; visible >= 0; visible--) {
    chips.forEach((chip, index) => (chip.hidden = index >= visible));
    count.textContent = "+" + (chips.length - visible);
    const starts = rows();
    if (!starts.includes(more.offsetTop)) starts.push(more.offsetTop);
    if (starts.length <= 2) break;
  }
}

const pending = new Set<HTMLElement>();
const observed = new WeakSet<HTMLElement>();
let flushQueued = false;

function queueLayout(root: HTMLElement) {
  pending.add(root);
  if (flushQueued) return;
  flushQueued = true;
  requestAnimationFrame(() => {
    const flush = () => {
      flushQueued = false;
      const lists = [...pending].filter((list) => list.isConnected);
      pending.clear();
      layoutAll(lists);
      for (const list of lists) {
        if (bindings.has(list) && !observed.has(list)) {
          observed.add(list);
          observer().observe(list);
        }
      }
    };
    if (typeof ResizeObserver === "undefined") {
      flush();
      return;
    }
    const once = new ResizeObserver(() => {
      once.disconnect();
      flush();
    });
    once.observe(document.documentElement);
  });
}

// Leer document.fonts.ready fuerza estilo y layout: se lee una vez por página y las listas se ajustan otra vez al final.
let fontsLoaded: Promise<void> | true | null = null;
function whenFontsLoad(root: HTMLElement) {
  if (fontsLoaded === true) return;
  fontsLoaded ??= document.fonts.ready.then(() => {
    fontsLoaded = true;
  });
  void fontsLoaded.then(() => queueLayout(root));
}

/** Ajusta una lista (y la observa: un cambio de ancho pide otro ajuste). */
export function fit(root: HTMLElement | null): void {
  if (!root) return;
  if (!bindings.has(root)) {
    bindings.add(root);
    whenFontsLoad(root);
  }
  queueLayout(root);
}

const bound = new Set<HTMLElement>();
export function dispose(root: HTMLElement): void {
  resized?.unobserve(root);
  bindings.delete(root);
  bound.delete(root);
  pending.delete(root);
  observed.delete(root);
}

/** Todas las listas de la tabla y de la lista del celular en una sola llamada (R5). */
export function fitAll(...roots: (Element | null)[]): void {
  for (const list of [...bound]) if (!list.isConnected) dispose(list);
  for (const root of roots) {
    if (!root) continue;
    for (const list of root.querySelectorAll<HTMLElement>(".c2-tags")) {
      fit(list);
      bound.add(list);
    }
  }
}

/** Suelta solo lo que enlazó fitAll; las etiquetas de la ficha siguen con su propio ajuste. */
export function disposeAll(): void {
  for (const list of [...bound]) dispose(list);
}
